import SvgCameraOn from '@/assets/recording/record_camera_on.svg';
import SvgCameraOff from '@/assets/recording/record_camera_off.svg';
import SvgMicrophoneOn from '@/assets/recording/record_microphone_on.svg';
import SvgMicrophoneOff from '@/assets/recording/record_microphone_off.svg';
import SvgScreenOn from '@/assets/recording/record_screen_on.svg';
import SvgScreenOff from '@/assets/recording/record_screen_off.svg';
import SvgFileOn from '@/assets/recording/record_file_on.svg';
import SvgFileOff from '@/assets/recording/record_file_off.svg';
import SvgRecordStart from '@/assets/recording/record-start.svg';
import SvgRecordStop from '@/assets/recording/record-stop.svg';
import SvgRecordEdit from '@/assets/recording/record-edit.svg';
import Icon from '@/components/iconfont/Icon.jsx';
import './index.less';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  openCamera,
  closeCamera,
  openScreen,
  closeScreen,
  openMicrophone,
  closeMicrophone,
  closeFile,
  openFile,
  setFileVideoUrl,
  setFileType,
  FILE_TYPE,
  setFileSlideUrls,
  RECORD_STATE,
  switchRecordState,
  RECORD_READY_COUNT,
  MIN_RECORD_TIME,
  isVideoFile,
  isSlideFile,
  MAX_RECORD_TIME,
  MAX_RECORD_TIME_FREE,
  setVolume,
} from '@/store/slices/recording.js';
import {
  captureVideoStream,
  closeAudioVolumeIndicator,
  getMediaDevices,
  localStreams,
  openAudioVolumeIndicator,
  STREAM_EVENTS,
} from '@/utils/media.js';
import {
  pauseRecordMedia,
  resumeRecordMedia,
  setCompletedRecordInfo,
  startRecordMedia,
  stopRecordMedia,
} from '@/utils/recordMedia.js';
import { createProject, deleteProject } from '@/api/upload.js';
import Upload from '@/components/upload/Upload.jsx';
import { assetController, projectController, shareController, userService } from '@/api/index.js';
import Assets from '@/pages/recording/Assets/index.jsx';
import Confirm from '@/components/confirm/Confirm.jsx';
import { useWebSocket } from '@/hooks/useWebSocket.js';
import { MessageEventType } from '@/utils/socket.js';
import ProcessingLogo from '@/assets/assets/slide-file.png';
import { useNavigate } from 'react-router-dom';
import message from '@/utils/message.jsx';
import SentryTracker from '@/utils/Sentry/sentryUtils.js';
import LoginComponent from '@/components/loginComponent/LoginComponent.jsx';
import { getToken } from '@/utils/cookie.js';
import { setUserDetailState } from '@/store/slices/userDetail.js';
import { CheckEquity } from '@/utils/CheckEquity.js';
import { useDrawer } from '@/hooks/DrawerHooks.js';
import { setProjectCount } from '@/store/slices/home.js';
import { useTranslation } from 'react-i18next';

const UPLOAD_ASSET_LIMIT = {
  [FILE_TYPE.VIDEO]: {
    accept: '.mp4',
    text: 'MP4 format. Less than 1G.',
    maxSize: 1024 * 1024 * 1024, // 1GB
  },
  [FILE_TYPE.SLIDE]: {
    accept: '.ppt,.pptx,.pdf',
    text: 'PPT, PPTX or PDF format. Less than 500MB.',
    maxSize: 500 * 1024 * 1024, // 500M
  },
};
let leavePage = false;
export default function ControlBar({ screenRecordCb, setOuterScreenLoading }) {
  const { setIsSubscribeOpen, setSubscribeTitle, setSubscribeOptions } = useDrawer();
  const cameraOn = useSelector((state) => state.recording.cameraOn);
  const microphoneOn = useSelector((state) => state.recording.microphoneOn);
  const screenOn = useSelector((state) => state.recording.screenOn);
  const fileOn = useSelector((state) => state.recording.fileOn);
  const fileType = useSelector((state) => state.recording.fileType);
  const dispatch = useDispatch();
  const state = useSelector((state) => state.recording.state);
  const volume = useSelector((state) => state.recording.volume);
  const readyRecordTimeout = useRef(null);
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const cameraMirror = useSelector((state) => state.recording.cameraMirror);
  const userDetail = useSelector((state) => state.userDetail.userDetailState);
  const isFreeUser = userDetail.userSubscription?.level === 1;
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const projectCount = useSelector((state) => state.home.projectCount);
  const { t } = useTranslation();

  useEffect(() => {
    setIsLogin(!!getToken());
  }, []);

  function checkAmountEnough(checkFreeProjectLimit, checkStorage, checkTranscript) {
    if (
      checkFreeProjectLimit &&
      !CheckEquity({ userData: userDetail, type: 'freeCount', projectCount })
    ) {
      setSubscribeTitle(t('As_Home_Limit1'));
      setSubscribeOptions({
        description: t('As_Home_Limit2'),
        confirmText: t('As_Home_Limit3'),
      });
      setIsSubscribeOpen('open');
      return false;
    }

    if (checkStorage && !CheckEquity({ userData: userDetail, type: 'storage' })) {
      setSubscribeTitle('Your storage space has been used up');
      setIsSubscribeOpen('open');
      return false;
    }
    if (checkTranscript && !CheckEquity({ userData: userDetail, type: 'transcript' })) {
      setSubscribeTitle('Your AI Transcription has been used up');
      setIsSubscribeOpen('open');
      return false;
    }
    return true;
  }

  async function handleLogin() {
    const [userRes, projectRes] = await Promise.all([
      userService.userDetail(),
      projectController.list(),
    ]);
    if (userRes?.code === 0) {
      dispatch(setUserDetailState({ key: 'userDetailState', value: userRes.data }));
    }
    if (projectRes.code === 0) {
      dispatch(setProjectCount(projectRes.data?.length || 0));
    }
    setIsLogin(true);
    setShowLoginModal(false);
  }

  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDevices, setAudioDevices] = useState([]);
  const setDeviceList = useCallback(async () => {
    const { videoDevices, audioDevices } = await getMediaDevices();
    if (videoDevices.length === 0) {
      videoDevices.push({
        label: 'No devices',
        deviceId: -1,
      });
    }
    if (audioDevices.length === 0) {
      audioDevices.push({
        label: 'No devices',
        deviceId: -1,
      });
    }
    setVideoDevices(videoDevices);
    setAudioDevices(audioDevices);
    return [videoDevices[0].deviceId, audioDevices[0].deviceId];
  }, []);

  useEffect(() => {
    toggleScreen();
    setDeviceList().then(([videoDeviceId, audioDeviceId]) => {
      setSelectedVideoDevice(videoDeviceId);
      setSelectedAudioDevice(audioDeviceId);
      resetRecordPage(videoDeviceId, audioDeviceId);
    });
    dispatch(switchRecordState(RECORD_STATE.NOT_STARTED));
    return async () => {
      try {
        leavePage = true;
        dispatch(closeCamera());
        localStreams.closeCameraStream();
        dispatch(closeMicrophone());
        closeAudioVolumeIndicator();
        localStreams.closeMicrophoneStream();
        dispatch(closeScreen());
        localStreams.closeScreenStream();
        dispatch(closeFile());
        dispatch(setFileVideoUrl(null));
        dispatch(setFileSlideUrls([]));
        disposeCaptureFile.current && disposeCaptureFile.current();
        if (readyRecordTimeout.current) {
          clearTimeout(readyRecordTimeout.current);
        }
        await stopRecordMedia();
      } catch (e) {
        console.warn('leave recording page', e);
      }
    };
  }, []);

  useEffect(() => {
    function updateDevice() {
      setDeviceList().then(([videoDeviceId, audioDeviceId]) => {
        if (!cameraOn) {
          setSelectedVideoDevice(videoDeviceId);
        }
        if (!microphoneOn) {
          setSelectedAudioDevice(audioDeviceId);
        }
      });
    }
    localStreams.on(STREAM_EVENTS.MEDIA_DEVICE_CHANGE, updateDevice);
    return () => {
      localStreams.off(STREAM_EVENTS.MEDIA_DEVICE_CHANGE, updateDevice);
    };
  }, [cameraOn, microphoneOn]);

  const videoDeviceListRef = useRef(null);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');
  function changeVideoDevice(id) {
    if (cameraOn && id === selectedVideoDevice) {
      return;
    }
    if (cameraOn) {
      closeLocalCamera();
    }
    setSelectedVideoDevice(id);
    openLocalCamera(id);
  }

  const audioDeviceListRef = useRef(null);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  async function changeAudioDevice(id) {
    if (microphoneOn && id === selectedAudioDevice) {
      return;
    }
    if (microphoneOn) {
      closeLocalMicrophone();
    }
    setSelectedAudioDevice(id);
    openLocalMicrophone(id);
  }

  const videoDevicesItem = videoDevices.map(({ deviceId, label }) => (
    <li
      onClick={() => changeVideoDevice(deviceId)}
      key={deviceId}
      className={`${deviceId === selectedVideoDevice && cameraOn ? 'selected' : ''}`}>
      <a>{label}</a>
    </li>
  ));

  const audioDevicesItem = audioDevices.map(({ deviceId, label }) => (
    <li
      onClick={() => changeAudioDevice(deviceId)}
      key={deviceId}
      className={`${deviceId === selectedAudioDevice && microphoneOn ? 'selected' : ''}`}>
      <a>{label}</a>
    </li>
  ));

  // 开关摄像头
  const [cameraLoading, setCameraLoading] = useState(false);
  const openLocalCamera = useCallback(async (deviceId) => {
    if (deviceId === -1) {
      return;
    }
    setCameraLoading(true);
    try {
      await localStreams.createCameraStream(deviceId);
      dispatch(openCamera());
    } catch {
      message.info('Please check your camera/microphone availability', {
        id: 'mediaDeviceFail',
      });
    }
    setCameraLoading(false);
  }, []);
  const closeLocalCamera = useCallback(() => {
    localStreams.closeCameraStream();
    dispatch(closeCamera());
  }, []);
  const toggleCamera = useCallback(async () => {
    window.onClickGATrackEvent('toggle-cam');
    if (cameraOn) {
      closeLocalCamera();
    } else {
      await openLocalCamera(selectedVideoDevice);
    }
  }, [cameraOn, selectedVideoDevice]);

  // 开关麦克风
  const [microphoneLoading, setMicrophoneLoading] = useState(false);
  const openLocalMicrophone = useCallback(async (deviceId) => {
    if (deviceId === -1) {
      return;
    }
    setMicrophoneLoading(true);
    try {
      const stream = await localStreams.createMicrophoneStream(deviceId);
      openAudioVolumeIndicator(stream, (v) => {
        dispatch(setVolume(v));
      });
      dispatch(openMicrophone());
    } catch {
      message.info('Please check your camera/microphone availability', {
        id: 'mediaDeviceFail',
      });
    }
    setMicrophoneLoading(false);
  }, []);
  const closeLocalMicrophone = useCallback(() => {
    closeAudioVolumeIndicator();
    localStreams.closeMicrophoneStream();
    dispatch(closeMicrophone());
  }, []);
  const toggleMicrophone = useCallback(async () => {
    window.onClickGATrackEvent('toggle-mic');
    if (microphoneOn) {
      closeLocalMicrophone();
    } else {
      await openLocalMicrophone(selectedAudioDevice);
    }
  }, [microphoneOn, selectedAudioDevice]);

  const [recordTime, setRecordTime] = useState(0);
  const recordTimeInterval = useRef(null);
  const calculateRecordTime = useCallback((state) => {
    recordTimeInterval.current && clearInterval(recordTimeInterval.current);
    if (state !== RECORD_STATE.IN_PROGRESS) {
      return;
    }
    recordTimeInterval.current = setInterval(() => {
      setRecordTime((time) => time + 0.1);
    }, 100);
  }, []);

  // 开关屏幕共享
  const [screenLoading, setScreenLoading] = useState(false);
  const toggleScreen = useCallback(async () => {
    if (screenOn) {
      localStreams.closeScreenStream();
      dispatch(closeScreen());
    } else {
      setScreenLoading(true);
      setOuterScreenLoading(true);
      const stream = await localStreams.createScreenStream();
      if (stream) {
        dispatch(openScreen());
        resolveFileScreenConflict('file');
      }
      setOuterScreenLoading(false);
      setScreenLoading(false);
    }
  }, [screenOn]);

  useEffect(() => {
    const timeLimit = isFreeUser ? MAX_RECORD_TIME_FREE : MAX_RECORD_TIME;
    if (
      recordTime >= timeLimit &&
      [RECORD_STATE.IN_PROGRESS, RECORD_STATE.PAUSED].includes(state)
    ) {
      stopRecord();
      return;
    }

    const handleStreamEnd = () => {
      if (state === RECORD_STATE.READY) {
        if (readyRecordTimeout.current) {
          clearTimeout(readyRecordTimeout.current);
        }
        dispatch(switchRecordState(RECORD_STATE.NOT_STARTED));
        return;
      }

      if ([RECORD_STATE.IN_PROGRESS, RECORD_STATE.PAUSED].includes(state)) {
        if (recordTime < MIN_RECORD_TIME) {
          forceStopWithoutCreate();
        } else {
          stopRecord();
        }
      }
    };
    const handleCameraEnd = () => {
      message.info('Please check your camera/microphone availability', {
        id: 'mediaDeviceFail',
      });
      if (cameraOn) {
        closeLocalCamera();
      }
      handleStreamEnd();
    };
    const handleMicrophoneEnd = () => {
      message.info('Please check your camera/microphone availability', {
        id: 'mediaDeviceFail',
      });
      if (microphoneOn) {
        closeLocalMicrophone();
      }
      handleStreamEnd();
    };
    const handleScreenEnd = () => {
      if (screenOn && recordTime < MIN_RECORD_TIME) {
        toggleScreen();
      }
      handleStreamEnd();
    };
    localStreams.on(STREAM_EVENTS.CAMERA_STREAM_CLOSE, handleCameraEnd);
    localStreams.on(STREAM_EVENTS.MICROPHONE_STREAM_CLOSE, handleMicrophoneEnd);
    localStreams.on(STREAM_EVENTS.SCREEN_STREAM_CLOSE, handleScreenEnd);
    return () => {
      localStreams.off(STREAM_EVENTS.CAMERA_STREAM_CLOSE, handleCameraEnd);
      localStreams.off(STREAM_EVENTS.MICROPHONE_STREAM_CLOSE, handleMicrophoneEnd);
      localStreams.off(STREAM_EVENTS.SCREEN_STREAM_CLOSE, handleScreenEnd);
    };
  }, [state, recordTime, cameraOn, microphoneOn, screenOn, fileOn]);
  screenRecordCb && screenRecordCb(toggleScreen);

  // 开关文件
  const toggleFile = useCallback(
    (e) => {
      if (fileOn) {
        if (e?.nativeEvent) {
          e.nativeEvent.stopImmediatePropagation();
          e.nativeEvent.preventDefault();
        }
        dispatch(setFileVideoUrl(null));
        dispatch(setFileSlideUrls([]));
        dispatch(closeFile());
      } else if (!isLogin) {
        if (e?.nativeEvent) {
          e.nativeEvent.stopImmediatePropagation();
          e.nativeEvent.preventDefault();
        }
        setShowLoginModal(true);
      }
    },
    [fileOn, isLogin],
  );

  function resolveFileScreenConflict(target) {
    if (target === 'screen') {
      localStreams.closeScreenStream();
      dispatch(closeScreen());
    } else if (target === 'file') {
      dispatch(setFileVideoUrl(null));
      dispatch(setFileSlideUrls([]));
      dispatch(closeFile());
    }
  }

  const [uploadPupUpModal, setUploadPupUpModal] = useState(false);
  const uploadAssetLimit = UPLOAD_ASSET_LIMIT[fileType];
  const fileListRef = useRef(null);
  const [processingSlideId, setProcessingSlideId] = useState(null);
  const onUploadSuccess = useCallback(
    async (val, file) => {
      const res = await assetController.add({
        name: file.name,
        uploadId: val.uploadId,
        completedParts: val.eTags,
      });
      if (res.code === 0) {
        if (isVideoFile(fileType)) {
          dispatch(setFileVideoUrl(res.data.asset.url));
          dispatch(openFile());
          resolveFileScreenConflict('screen');
        } else if (isSlideFile(fileType)) {
          setProcessingSlideId(res.data.asset.id);
        }
      }
    },
    [fileType],
  );

  const uploadContent = useMemo(
    () => (
      <Upload
        value={{
          uploadPupUpModal,
          setUploadPupUpModal,
          onUploadSuccess,
          usage: 4,
          ...uploadAssetLimit,
        }}
      />
    ),
    [uploadPupUpModal, setUploadPupUpModal, onUploadSuccess],
  );

  useWebSocket(MessageEventType.USER_ASSET_DETAILS, ({ assetId, details }) => {
    if (assetId === processingSlideId) {
      dispatch(setFileSlideUrls(details.map((d) => d.url)));
      dispatch(openFile());
      resolveFileScreenConflict('screen');
      setProcessingSlideId(null);
    }
  });

  function openUploadSlide() {
    window.onClickGATrackEvent('file-upload-slides');
    if (checkAmountEnough(false, true)) {
      dispatch(setFileType(FILE_TYPE.SLIDE));
      setUploadPupUpModal(true);
    }
  }

  function openUploadVideo() {
    window.onClickGATrackEvent('file-upload-video');
    if (checkAmountEnough(false, true)) {
      dispatch(setFileType(FILE_TYPE.VIDEO));
      setUploadPupUpModal(true);
    }
  }

  const [showAssets, setShowAssets] = useState(false);
  function openAssets() {
    setShowAssets(true);
  }

  function handleSelectAsset({ slideUrls, videoUrl }) {
    if (slideUrls) {
      dispatch(setFileType(FILE_TYPE.SLIDE));
      dispatch(setFileSlideUrls(slideUrls));
      dispatch(openFile());
      resolveFileScreenConflict('screen');
    } else if (videoUrl) {
      dispatch(setFileType(FILE_TYPE.VIDEO));
      dispatch(setFileVideoUrl(videoUrl));
      dispatch(openFile());
      resolveFileScreenConflict('screen');
    }
  }

  useEffect(() => {
    const closeVideoDevice = (e) => {
      const { target } = e;
      if (!target.matches('.video-device-btn')) {
        videoDeviceListRef.current.removeAttribute('open');
      }

      if (!target.matches('.audio-device-btn')) {
        audioDeviceListRef.current.removeAttribute('open');
      }

      if (!target.matches('.file-device-btn')) {
        fileListRef.current.removeAttribute('open');
      }
    };
    document.addEventListener('click', closeVideoDevice);

    return () => {
      document.removeEventListener('click', closeVideoDevice);
    };
  }, []);

  function readyRecord() {
    if (!cameraOn && !microphoneOn && !screenOn && !fileOn) {
      toggleScreen();
      return;
    }

    if (!isLogin) {
      setShowLoginModal(true);
      return;
    }

    if (checkAmountEnough(true, true, true)) {
      SentryTracker.captureEvent('recordpage_start_record_click', userInfo?.email);
      dispatch(switchRecordState(RECORD_STATE.READY));
      readyRecordTimeout.current = setTimeout(startRecord, RECORD_READY_COUNT * 1000);
    }
  }

  const disposeCaptureFile = useRef(null);
  function startRecord() {
    const streams = {
      camera: null,
      microphone: null,
      screen: null,
      file: null,
    };

    if (cameraOn) {
      streams.camera = localStreams.getCameraStream();
    }

    if (microphoneOn) {
      streams.microphone = localStreams.getMicrophoneStream();
    }

    if (screenOn) {
      streams.screen = localStreams.getScreenStream();
    }

    if (fileOn) {
      if (isVideoFile(fileType)) {
        const video = document.querySelector('.file-wrapper video');
        const { stream, dispose } = captureVideoStream(video);
        streams.file = stream;
        disposeCaptureFile.current = dispose;
      } else if (isSlideFile(fileType)) {
        const canvas = document.querySelector('.slide-player canvas');
        const stream = canvas.captureStream();
        streams.file = stream;
        disposeCaptureFile.current = () => {
          stream.getTracks().forEach((track) => track.stop());
        };
      }
    }

    startRecordMedia(streams, {
      cameraMirror: cameraMirror,
    }).then(() => {
      dispatch(switchRecordState(RECORD_STATE.IN_PROGRESS));
      calculateRecordTime(RECORD_STATE.IN_PROGRESS);
    });
  }

  function pauseRecord() {
    window.onClickGATrackEvent('pause-play');
    pauseRecordMedia();
    dispatch(switchRecordState(RECORD_STATE.PAUSED));
    calculateRecordTime(RECORD_STATE.PAUSED);
  }

  function resumeRecord() {
    window.onClickGATrackEvent('pause-play');
    resumeRecordMedia();
    dispatch(switchRecordState(RECORD_STATE.IN_PROGRESS));
    calculateRecordTime(RECORD_STATE.IN_PROGRESS);
  }

  const projectId = useRef(null);
  const [isStopping, setIsStopping] = useState(false);
  async function stopRecord() {
    if (recordTime < MIN_RECORD_TIME) {
      message.info('Please record the video for more than 3 seconds', {
        id: 'recordTime',
        duration: 3000,
      });
      return;
    }
    if (isStopping) {
      return;
    }
    leavePage = false;
    SentryTracker.captureEvent('recordpage_done_record_click', userInfo?.email);
    try {
      setIsStopping(true);
      calculateRecordTime(RECORD_STATE.COMPLETED);
      const recordData = await stopRecordMedia();
      recordData.duration = Math.floor(recordTime * 1000);
      setCompletedRecordInfo(recordData.name, recordTime);
      if (leavePage) {
        createProject(recordData);
        return;
      }
      dispatch(switchRecordState(RECORD_STATE.COMPLETED));
      const res = await createProject(recordData);
      if (leavePage) {
        return;
      }
      projectId.current = res.data;
      dispatch(switchRecordState(RECORD_STATE.CREATED));
      closeAllStream();
      preDownloadProjectVideo(projectId.current);
      toView();
    } catch {
      if (screenOn) {
        toggleScreen();
      }
      if (fileOn) {
        disposeCaptureFile.current && disposeCaptureFile.current();
        toggleFile();
      }
      resetRecordPage();
    } finally {
      setIsStopping(false);
    }
  }

  function preDownloadProjectVideo(projectId) {
    shareController.preDownloadVideo(projectId);
  }

  function toView() {
    sessionStorage.setItem(
      'projectItem',
      JSON.stringify({
        id: projectId.current,
      }),
    );
    navigate('/view');
  }

  async function forceStopWithoutCreate() {
    calculateRecordTime(RECORD_STATE.COMPLETED);
    dispatch(switchRecordState(RECORD_STATE.NOT_STARTED));
    await stopRecordMedia();
    if (fileOn) {
      disposeCaptureFile.current && disposeCaptureFile.current();
      toggleFile();
    }
    resetRecordPage();
  }

  function closeAllStream() {
    if (cameraOn) {
      closeLocalCamera();
    }
    if (microphoneOn) {
      closeLocalMicrophone();
    }
    if (screenOn) {
      toggleScreen();
    }
    if (fileOn) {
      disposeCaptureFile.current && disposeCaptureFile.current();
      toggleFile();
    }
  }

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  function deleteCurrentProject() {
    deleteProject(projectId.current)
      .then((res) => {
        if (res.code === 0) {
          resetRecordPage();
        } else {
          throw new Error(res);
        }
      })
      .catch(() => {
        message.error('Network error', {
          id: 'deleteProjectFail',
        });
      });
  }

  function resetRecordPage(videoDeviceId, audioDeviceId) {
    setRecordTime(0);
    dispatch(switchRecordState(RECORD_STATE.NOT_STARTED));
    if (!cameraOn) {
      openLocalCamera(videoDeviceId || selectedVideoDevice);
    }
    if (!microphoneOn) {
      openLocalMicrophone(audioDeviceId || selectedAudioDevice);
    }
  }

  function toEdit() {
    SentryTracker.captureEvent('recordpage_preview_edit_click', userInfo?.email);
    sessionStorage.setItem(
      'projectItem',
      JSON.stringify({
        id: projectId.current,
      }),
    );
    navigate('/edit');
  }

  return (
    <>
      {showLoginModal && (
        <LoginComponent
          setLoginFlag={setShowLoginModal}
          setUserDetailUpdate={handleLogin}></LoginComponent>
      )}

      <div className={`control-stream ${state !== RECORD_STATE.NOT_STARTED ? 'hide' : ''}`}>
        <div className="group">
          <button
            className={`btn btn-ghost main-button ${cameraOn ? 'on' : ''} ${cameraLoading ? 'in-loading' : ''}`}
            onClick={toggleCamera}
            disabled={cameraLoading}>
            <img src={cameraOn ? SvgCameraOn : SvgCameraOff} alt="" />
            <span>{t('As_Recording_Camera')}</span>
            <span className="loading loading-spinner"></span>
          </button>

          <details ref={videoDeviceListRef} className="dropdown dropdown-top">
            <summary className="btn btn-ghost icon video-device-btn">
              <Icon name="icon-arrow-down video-device-btn"></Icon>
            </summary>
            <ul className="menu dropdown-content bg-base-100 rounded-box z-[1] p-2 shadow">
              {videoDevicesItem}
            </ul>
          </details>
        </div>

        <div className="group">
          <button
            className={`btn btn-ghost main-button ${microphoneOn ? 'on' : ''} ${microphoneLoading ? 'in-loading' : ''}`}
            onClick={toggleMicrophone}
            disabled={microphoneLoading}>
            {microphoneOn ? (
              <i className="mini-volume-indicator" style={{ height: volume * 16 + 'px' }}></i>
            ) : null}
            <img src={microphoneOn ? SvgMicrophoneOn : SvgMicrophoneOff} alt="" />
            <span>{t('As_Recording_Audio')}</span>
            <span className="loading loading-spinner"></span>
          </button>

          <details ref={audioDeviceListRef} className="dropdown dropdown-top">
            <summary className="btn btn-ghost icon audio-device-btn">
              <Icon name="icon-arrow-down audio-device-btn"></Icon>
            </summary>
            <ul className="menu dropdown-content bg-base-100 rounded-box z-[1] p-2 shadow">
              {audioDevicesItem}
            </ul>
          </details>
        </div>

        <button
          className={`btn btn-ghost main-button ${screenOn ? 'on' : ''}  ${screenLoading ? 'in-loading' : ''}`}
          style={{ marginRight: '48px' }}
          onClick={() => {
            window.onClickGATrackEvent('screen-share');
            toggleScreen();
          }}
          disabled={screenLoading}>
          <img src={screenOn ? SvgScreenOn : SvgScreenOff} alt="" />
          <span>{t('As_Recording_Share')}</span>
          <span className="loading loading-spinner"></span>
        </button>

        <details ref={fileListRef} className="dropdown dropdown-top file">
          <summary
            className={`btn btn-ghost main-button ${fileOn ? 'on' : ''} file-device-btn`}
            onClick={() => {
              window.onClickGATrackEvent('file-share');
              toggleFile();
            }}>
            <img src={fileOn ? SvgFileOn : SvgFileOff} alt="" className="file-device-btn" />
            <span className="file-device-btn">{t('As_Recording_Slides')}</span>
          </summary>
          <ul className="menu dropdown-content bg-base-100 rounded-box z-[1] p-2 shadow">
            <li onClick={openUploadSlide}>
              <a>
                <Icon name="icon-upload"></Icon>
                {t('As_Recording_UploadSlides')}
              </a>
            </li>
            <li onClick={openUploadVideo}>
              <a>
                <Icon name="icon-upload"></Icon>
                {t('As_Recording_UploadVideo')}
              </a>
            </li>
            <li onClick={openAssets}>
              <a>
                <Icon name="icon-lib"></Icon>
                {t('As_Recording_Import')}
              </a>
            </li>
          </ul>
        </details>

        {uploadContent}

        <Assets
          show={showAssets}
          onClose={() => setShowAssets(false)}
          onSelect={handleSelectAsset}></Assets>

        {processingSlideId ? (
          <div className="modal process-slide" role="dialog" open>
            <div className="modal-box">
              <h3 className="text-lg font-bold">{t('As_Recording_SlideProcessing1')}</h3>

              <div className="process-content">
                <div className="process-tip">
                  <img src={ProcessingLogo} alt="" />
                  <div>{t('As_Recording_SlideProcessing2')}</div>
                </div>

                <div>
                  <button className="btn btn-sm">
                    <span className="loading loading-spinner loading-sm"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <button
          className="btn btn-error start-record"
          onClick={() => {
            window.onClickGATrackEvent('start-recording');
            readyRecord();
          }}
          disabled={cameraLoading || microphoneLoading || screenLoading}>
          <img src={SvgRecordStart} alt="" />
          <span>{t('As_Recording_StartRecording')}</span>
        </button>
      </div>

      <div
        className={`control-recording ${![RECORD_STATE.READY, RECORD_STATE.IN_PROGRESS, RECORD_STATE.PAUSED, RECORD_STATE.COMPLETED].includes(state) ? 'hide' : ''}`}>
        <p className="record-time">
          <span className="current">
            {String(Math.floor(recordTime / 60)).padStart(2, '0')}:
            {String(Math.floor(recordTime % 60)).padStart(2, '0')}
          </span>
          <span className="limit">&nbsp;/&nbsp;{isFreeUser ? '05:00' : '30:00'}</span>
        </p>

        {state === RECORD_STATE.PAUSED ? (
          <button
            className="btn pause"
            onClick={resumeRecord}
            disabled={state !== RECORD_STATE.PAUSED || isStopping}>
            <Icon name="icon-play-rounded"></Icon>
            <span>{t('As_Recording_Resume')}</span>
          </button>
        ) : (
          <button
            className="btn pause"
            onClick={pauseRecord}
            disabled={state !== RECORD_STATE.IN_PROGRESS || isStopping}>
            <Icon name="icon-pause-rounded"></Icon>
            <span>{t('As_Recording_Pause')}</span>
          </button>
        )}

        <button
          className="btn btn-error"
          onClick={() => {
            window.onClickGATrackEvent('stop-recording');
            stopRecord();
          }}
          disabled={![RECORD_STATE.IN_PROGRESS, RECORD_STATE.PAUSED].includes(state) || isStopping}>
          {isStopping ? (
            <span className="loading loading-spinner"></span>
          ) : (
            <img src={SvgRecordStop} alt="" />
          )}
          <span>{t('As_Recording_Stop')}</span>
        </button>
      </div>

      <div
        className={`control-record-ended ${![RECORD_STATE.CREATED].includes(state) ? 'hide' : ''}`}>
        <button
          className="btn"
          style={{ minHeight: '38px', height: '38px' }}
          onClick={() => setShowDeleteDialog(true)}
          disabled={state !== RECORD_STATE.CREATED}>
          Delete
        </button>

        <Confirm
          show={showDeleteDialog}
          title="Are you sure to delete?"
          content="This will permanently delete the recorded video"
          okText="Delete"
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={deleteCurrentProject}></Confirm>

        {state === RECORD_STATE.CREATED ? (
          <button className="btn btn-primary" onClick={toEdit}>
            <img src={SvgRecordEdit} alt="" />
            <span>Edit video & Export</span>
          </button>
        ) : (
          <button className="btn btn-primary" style={{ width: '224px' }}>
            <span className="loading loading-spinner"></span>
            <span>Please wait seconds</span>
          </button>
        )}
      </div>
    </>
  );
}
